import {
  createNotificationAction,
  deleteNotificationAction,
  getNotificationAction,
} from "@/actions/notification";
import { useMarket } from "@features/market/useMarket";
import useAuth from "@features/auth/hooks/useAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

type UseSignUpProps = {
  productId: string;
  variantId?: string;
};

export default function useSignUp({ productId, variantId }: UseSignUpProps) {
  const { user, token } = useAuth();
  const market = useMarket();
  const queryClient = useQueryClient();

  const { data: active, isLoading } = useQuery({
    queryKey: ["notification", token, productId],
    queryFn: async () => {
      if (!token || !variantId) return false;
      const response = await getNotificationAction({
        token,
        locale: market.locale,
        productId,
      });
      return response.variantIdsWithNotifications?.includes(variantId) ?? false;
    },
    enabled: Boolean(token && variantId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { mutate: signup, isPending: signingUp } = useMutation({
    mutationFn: async () => {
      if (!token || !variantId) return;
      return createNotificationAction({
        token,
        locale: market.locale,
        productId,
        variantId,
      });
    },
    onSuccess: () => {
      queryClient.setQueryData(["notification", token, productId], true);
      void queryClient.invalidateQueries({ queryKey: ["notification", token] });
    },
  });
  const { mutate: signoff, isPending: signingOff } = useMutation({
    mutationFn: async () => {
      if (!token || !variantId) return;
      return deleteNotificationAction({
        token,
        locale: market.locale,
        productId,
        variantId,
      });
    },
    onSuccess: () => {
      queryClient.setQueryData(["notification", token, productId], false);
      void queryClient.invalidateQueries({ queryKey: ["notification", token] });
    },
  });

  const toggleNotification = useCallback(() => {
    if (!user) return;
    if (active) {
      signoff();
    } else {
      signup();
    }
  }, [user, active, signup, signoff]);

  const loading = isLoading || signingUp || signingOff;

  return { active, toggleNotification, loading };
}
